import { useCallback, useEffect, useRef, useState } from 'react'
import { candidatosEanApi } from '../api/candidatosEan'
import { fmtFechaHora } from '../utils/fecha'
import { colors, text, space, radius, sx } from '../design/tokens'
import Boton from './Boton'
import Modal from './Modal'
import ImagenAutenticada from './ImagenAutenticada'
import SelectorArticulosModal from './SelectorArticulosModal'

/**
 * Búsqueda por CÓDIGO DE BARRAS (Martin, 29 sep 2026): se tipea el EAN (o se
 * importa una planilla de EANs) y el agente trae la foto —ya normalizada al
 * canon del catálogo— y la ficha técnica con su fuente. La persona revisa,
 * elige el SKU con el selector de artículos y aprueba: recién ahí se publica.
 * Las búsquedas corren en cola, de a una (tardan unos minutos cada una).
 */
export default function ContenidoPorEan() {
  const [data, setData] = useState({ activas: [], sin_resultado: [], campos: {}, max_importacion: 300 })
  const [ean, setEan] = useState('')
  const [busy, setBusy] = useState(null)
  const [err, setErr] = useState('')
  const [zoom, setZoom] = useState(null)
  const [importando, setImportando] = useState(false)
  const [eligiendo, setEligiendo] = useState(false)
  const [aviso, setAviso] = useState(null) // {encolados, omitidos}

  const [leidoAt, setLeidoAt] = useState(() => Date.now())
  const cargar = useCallback(() => candidatosEanApi.listar()
    .then(({ data }) => { setData(data); setLeidoAt(Date.now()) })
    .catch(() => setErr('No se pudieron cargar las búsquedas por código de barras.')), [])
  useEffect(() => { cargar() }, [cargar])

  // Mientras haya búsquedas en curso, se consulta cada 5 s.
  const enCurso = data.activas.filter((c) => c.estado === 'en_cola' || c.estado === 'buscando')
  useEffect(() => {
    if (enCurso.length === 0) return
    const t = setInterval(cargar, 5000)
    return () => clearInterval(t)
  }, [enCurso.length, cargar])

  const buscar = async (codigo) => {
    const valor = (codigo ?? ean).trim()
    if (!valor) return
    setBusy('buscar'); setErr('')
    try {
      await candidatosEanApi.buscar(valor)
      if (codigo === undefined) setEan('')
      cargar()
    } catch (e) {
      setErr(e?.response?.data?.message || `No se pudo buscar ${valor}.`)
    } finally { setBusy(null) }
  }

  // Artículos elegidos en el selector (con foto y EAN): se busca por el EAN de cada uno.
  const buscarElegidos = async (items) => {
    setBusy('buscar'); setErr(''); setAviso(null)
    try {
      const { data: r } = await candidatosEanApi.buscarSkus(items.map(({ articulo }) => articulo.codigo_alfa))
      setAviso(r)
      cargar()
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudieron enviar los artículos a buscar.')
    } finally { setBusy(null) }
  }

  const pendientes = data.activas.filter((c) => c.estado === 'pendiente')

  return (
    <section>
      <div style={st.barra}>
        <input value={ean} onChange={(e) => setEan(e.target.value)} placeholder="Código de barras (EAN)" inputMode="numeric"
          style={st.input} onKeyDown={(e) => { if (e.key === 'Enter') buscar() }} autoFocus />
        <Boton variante="primary" onClick={() => buscar()} disabled={busy === 'buscar' || !ean.trim()}>{busy === 'buscar' ? 'Enviando…' : 'Buscar'}</Boton>
        <Boton variante="secondary" onClick={() => setEligiendo(true)} disabled={busy === 'buscar'}>🔎 Elegir por SKU</Boton>
        <Boton variante="secondary" onClick={() => setImportando(true)}>⇪ Importar planilla</Boton>
      </div>

      {err && <div style={st.err}>{err}</div>}
      {aviso && (
        <div style={st.aviso}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span><strong>{aviso.encolados}</strong> {aviso.encolados === 1 ? 'artículo enviado' : 'artículos enviados'} a buscar en la web.
              {aviso.omitidos.length > 0 && ` ${aviso.omitidos.length} ${aviso.omitidos.length === 1 ? 'quedó afuera' : 'quedaron afuera'}:`}</span>
            <button type="button" onClick={() => setAviso(null)} style={st.cerrar} aria-label="Cerrar">×</button>
          </div>
          {aviso.omitidos.length > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {aviso.omitidos.map((o) => <li key={o.sku}><span style={sx.mono}>{o.sku}</span> — {o.motivo}</li>)}
            </ul>
          )}
        </div>
      )}

      <PanelProgreso p={data.progreso} leidoAt={leidoAt} />

      {pendientes.length === 0 && enCurso.length === 0 && (
        <div style={st.vacio}>No hay búsquedas esperando aprobación. Tipeá un código de barras o importá una planilla.</div>
      )}

      <div style={{ display: 'grid', gap: space.lg }}>
        {pendientes.map((c) => (
          <TarjetaCandidato key={c.id} c={c} campos={data.campos} onZoom={() => setZoom(c)} onListo={cargar} />
        ))}
      </div>

      {data.sin_resultado.length > 0 && (
        <>
          <h2 style={st.h2}>Códigos sin resultado ({data.sin_resultado.length})</h2>
          <table style={st.table}>
            <thead><tr><th style={st.th}>EAN</th><th style={st.th}>Por qué</th><th style={st.th}>Buscado</th><th style={st.th}></th></tr></thead>
            <tbody>
              {data.sin_resultado.map((c) => (
                <tr key={c.id} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                  <td style={{ ...st.td, ...sx.mono }}>{c.ean}</td>
                  <td style={{ ...st.td, color: colors.textMeta, maxWidth: 620 }}>{c.motivo}</td>
                  <td style={{ ...st.td, color: colors.textMeta, whiteSpace: 'nowrap' }}>{fmtFechaHora(c.buscada_at)}</td>
                  <td style={{ ...st.td, whiteSpace: 'nowrap' }}>
                    <Boton variante="secondary" sm disabled={busy === 'buscar'} onClick={() => buscar(c.ean)}>↻ Buscar de nuevo</Boton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {zoom && (
        <div style={st.overlay} onClick={() => setZoom(null)}>
          <div style={st.zoomBox} onClick={(e) => e.stopPropagation()}>
            <ImagenAutenticada url={zoom.preview} alt={zoom.ean} style={{ width: 600, height: 600, maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', background: '#fff' }} />
            <div style={{ padding: '10px 14px', fontSize: text.body }}><strong>{zoom.ean}</strong> · {zoom.nombre}</div>
          </div>
        </div>
      )}

      {eligiendo && (
        <SelectorArticulosModal modo="multiple" conFoto soloConEan sinCantidad textoConfirmar="🌐 Buscar en web"
          titulo="Elegir artículos con código de barras para buscar sus datos en la web"
          onConfirm={buscarElegidos} onClose={() => setEligiendo(false)} />
      )}

      {importando && <ImportarEans max={data.max_importacion} onClose={() => setImportando(false)} onListo={() => { setImportando(false); cargar() }} />}
    </section>
  )
}

const mmss = (seg) => {
  const s = Math.max(0, Math.round(seg))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
const minutos = (seg) => {
  const m = Math.max(1, Math.round(seg / 60))
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`
}

/**
 * Qué está haciendo la cola, en vivo (Martin, 30 sep 2026: "no es claro si
 * está trabajando o no; necesito que la pantalla haga algo si está buscando y
 * qué es lo que está haciendo"). El reloj corre en la pantalla entre lectura y
 * lectura; el paso lo informa el servidor.
 */
function PanelProgreso({ p, leidoAt }) {
  const [ahora, setAhora] = useState(() => Date.now())
  const activo = (p?.buscando?.length || 0) + (p?.en_cola?.length || 0) > 0
  useEffect(() => {
    if (!activo) return
    const t = setInterval(() => setAhora(Date.now()), 1000)
    return () => clearInterval(t)
  }, [activo])
  if (!p) return null
  const corrido = (ahora - leidoAt) / 1000
  const tipico = p.segundos_tipico || 200
  const actual = p.buscando[0] || null
  const enEspera = p.en_cola
  const restante = actual ? Math.max(tipico - (actual.segundos + corrido), 30) : 0
  const faltaTotal = restante + enEspera.length * tipico
  const trabada = actual && actual.segundos + corrido > 15 * 60

  if (!activo) {
    if (!p.recientes?.length) return null
    return (
      <div style={st.panelQuieto}>
        <strong>✓ No hay búsquedas en curso.</strong> <span style={{ color: colors.textSec }}>Lo último que terminó:</span>
        <Recientes items={p.recientes} ahora={ahora} />
      </div>
    )
  }

  return (
    <div style={st.panel}>
      <style>{'@keyframes lgr-spin { to { transform: rotate(360deg); } } @keyframes lgr-barra { 0% { left: -35%; } 100% { left: 100%; } }'}</style>
      <div style={st.panelHead}>
        <span style={st.spinner} aria-hidden />
        <strong>Trabajando</strong>
        <span style={{ color: colors.textSec }}>
          {actual ? '1 buscando' : 'por arrancar'}{enEspera.length > 0 ? ` · ${enEspera.length} en espera` : ''} · termina en ~{minutos(faltaTotal)}
        </span>
      </div>

      {actual ? (
        <div style={st.ahora}>
          <div style={st.label}>Ahora</div>
          <div style={st.ahoraNombre}>
            <span style={sx.mono}>{actual.ean}</span>{actual.detalle ? <span> · {actual.detalle}</span> : null}
          </div>
          <div style={st.paso}>{actual.paso || 'Preparando la búsqueda'}{actual.segundos_paso != null ? <span style={st.meta}> · hace {mmss(actual.segundos_paso + corrido)}</span> : null}</div>
          <div style={st.barra2}><div style={st.barraMov} /></div>
          <div style={st.meta}>
            Buscando hace <span style={sx.mono}>{mmss(actual.segundos + corrido)}</span> · una búsqueda tarda ~{minutos(tipico)}
            {trabada && <span style={st.trabada}> · lleva más de 15 min: si no avanza, se reintenta sola</span>}
          </div>
        </div>
      ) : (
        <div style={st.ahora}>
          <div style={st.paso}>Por arrancar: el servidor toma la cola cada minuto.</div>
        </div>
      )}

      {enEspera.length > 0 && (
        <div style={{ marginTop: space.lg }}>
          <div style={st.label}>En espera ({enEspera.length})</div>
          <div style={st.chips}>
            {enEspera.slice(0, 30).map((c, i) => (
              <span key={c.id} style={st.chip} title={c.detalle || ''}><span style={sx.mono}>{i + 1}. {c.ean}</span>{c.detalle ? ` · ${c.detalle}` : ''}</span>
            ))}
            {enEspera.length > 30 && <span style={st.chip}>+{enEspera.length - 30}</span>}
          </div>
        </div>
      )}

      {p.recientes?.length > 0 && (
        <div style={{ marginTop: space.lg }}>
          <div style={st.label}>Terminaron recién</div>
          <Recientes items={p.recientes} ahora={ahora} />
        </div>
      )}
    </div>
  )
}

const RESULTADO = {
  pendiente: { icono: '✓', texto: 'encontrado — abajo, esperando tu aprobación', color: colors.ok },
  aprobada: { icono: '✓', texto: 'aprobado', color: colors.ok },
  rechazada: { icono: '—', texto: 'rechazado', color: colors.textSec },
  no_encontrada: { icono: '✕', texto: 'sin resultado', color: colors.textSec },
  error: { icono: '!', texto: 'error', color: colors.err },
}

function Recientes({ items, ahora }) {
  return (
    <div style={{ display: 'grid', gap: 2, marginTop: space.xs }}>
      {items.map((r) => {
        const cfg = RESULTADO[r.estado] || { icono: '·', texto: r.estado, color: colors.textSec }
        const hace = Math.round((ahora - new Date(r.terminada_at).getTime()) / 60000)
        return (
          <div key={r.id} style={st.reciente}>
            <span style={{ color: cfg.color, fontWeight: 700, width: 14 }}>{cfg.icono}</span>
            <span style={sx.mono}>{r.ean}</span>
            {r.nombre && <span>· {r.nombre}</span>}
            <span style={{ color: cfg.color }}>— {cfg.texto}</span>
            <span style={st.meta}>{hace <= 0 ? 'recién' : `hace ${hace} min`}</span>
          </div>
        )
      })}
    </div>
  )
}

/** Una búsqueda con resultado: foto + ficha, elección del SKU y aprobación. */
function TarjetaCandidato({ c, campos, onZoom, onListo }) {
  const [elegido, setElegido] = useState(null) // {sku, descripcion, tiene_foto, tiene_ficha}
  const [selector, setSelector] = useState(false)
  const [foto, setFoto] = useState(c.tiene_foto)
  const [ficha, setFicha] = useState(Object.keys(c.datos || {}).length > 0)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const elegir = useCallback(async (sku) => {
    setErr('')
    try {
      const { data } = await candidatosEanApi.estadoSku(sku)
      setElegido(data)
    } catch {
      setErr(`No se encontró el artículo ${sku}.`)
    }
  }, [])

  // El SKU que ya tiene este EAN en el maestro (o el de la planilla) viene preelegido: se puede cambiar.
  const sugeridoRef = useRef(false)
  useEffect(() => {
    if (c.sku_sugerido && !sugeridoRef.current) { sugeridoRef.current = true; elegir(c.sku_sugerido) }
  }, [c.sku_sugerido, elegir])

  const aprobar = async () => {
    setBusy(true); setErr('')
    try {
      await candidatosEanApi.aprobar(c.id, elegido.sku, foto, ficha)
      onListo()
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo aprobar.')
      setBusy(false)
    }
  }
  const descartar = async () => {
    const motivo = window.prompt(`¿Por qué se descarta el resultado de ${c.ean}? (opcional)`)
    if (motivo === null) return
    setBusy(true); setErr('')
    try {
      await candidatosEanApi.rechazar(c.id, motivo)
      onListo()
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo descartar.')
      setBusy(false)
    }
  }

  const tieneFicha = Object.keys(c.datos || {}).length > 0
  const v = c.verificacion
  const pct = c.confianza == null ? null : Math.round(c.confianza * 100)

  return (
    <article style={st.card}>
      <button type="button" onClick={c.tiene_foto ? onZoom : undefined} style={st.thumbBtn} title={c.tiene_foto ? 'Ver grande' : ''}>
        {c.tiene_foto
          ? <ImagenAutenticada url={c.preview} alt={c.ean} style={st.thumb} />
          : <div style={st.sinImg}>sin foto</div>}
      </button>

      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: space.md }}>
        <header>
          <div style={{ ...sx.mono, fontSize: text.sec, color: colors.textMeta }}>
            EAN {c.ean}{c.lote ? ` · importación ${c.lote}` : ''}
          </div>
          <div style={st.nombre}>{c.nombre}</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: text.sec }}>
            {pct != null && <span style={{ fontWeight: 600, color: pct >= 80 ? colors.ok : pct >= 50 ? colors.warn : colors.err }}>Confianza {pct} %</span>}
            {tieneFicha && (
              <span style={{ ...sx.badge, background: c.fuente_oficial ? colors.okBg : colors.warnBg, color: c.fuente_oficial ? colors.ok : colors.warn }}>
                {c.fuente_oficial ? 'sitio de la bodega' : 'otra fuente'}
              </span>
            )}
          </div>
          {v && (
            <div style={{ fontSize: text.sec, marginTop: 4 }}>
              {v.coincide === false && <div style={st.alerta}>⚠ la verificación de la foto duda de que sea el mismo producto</div>}
              {v.fondo_blanco === false && <div style={st.alerta}>⚠ fondo no blanco</div>}
              {v.observacion && <div style={{ color: colors.textMeta }}>{v.observacion}</div>}
            </div>
          )}
          {c.motivo && <div style={{ fontSize: text.sec, color: colors.textMeta, marginTop: 4 }}>{c.motivo}</div>}
        </header>

        {tieneFicha && (
          <div style={st.ficha}>
            {Object.entries(campos).map(([clave, label]) => (c.datos?.[clave] ? (
              <div key={clave} style={{ fontSize: text.body, lineHeight: 1.4 }}>
                <span style={st.label}>{label}</span>
                <div>{c.datos[clave]}</div>
              </div>
            ) : null))}
          </div>
        )}
        {c.descripcion && <p style={st.descripcion}>{c.descripcion}</p>}

        <div style={{ fontSize: text.sec, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {c.fuente_url && <a href={c.fuente_url} target="_blank" rel="noreferrer" style={st.link}>Fuente de la ficha ↗</a>}
          {c.imagen_url && <a href={c.imagen_url} target="_blank" rel="noreferrer" style={st.link}>Imagen original ↗</a>}
        </div>

        <footer style={st.pie}>
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={st.label}>SKU</span>
              {elegido
                ? <span><span style={{ ...sx.mono, fontWeight: 600 }}>{elegido.sku}</span> <span style={{ color: colors.textSec }}>{elegido.descripcion}</span></span>
                : <span style={{ color: colors.textMeta }}>sin elegir</span>}
              <Boton variante="secondary" sm onClick={() => setSelector(true)} disabled={busy}>{elegido ? 'Cambiar' : 'Elegir artículo…'}</Boton>
            </div>
            {elegido && c.sku_sugerido && elegido.sku === c.sku_sugerido && (
              <div style={{ fontSize: text.sec, color: colors.textMeta }}>
                {c.lote ? 'Vino en la planilla junto a este código.' : 'Es el artículo que tiene este código de barras en el maestro.'}
              </div>
            )}
            <div style={{ display: 'flex', gap: 16, fontSize: text.body, flexWrap: 'wrap' }}>
              <label style={st.check}>
                <input type="checkbox" checked={foto} disabled={!c.tiene_foto} onChange={(e) => setFoto(e.target.checked)} /> Publicar la foto
                {elegido?.tiene_foto && foto && <span style={st.alerta}> (reemplaza la que tiene)</span>}
              </label>
              <label style={st.check}>
                <input type="checkbox" checked={ficha} disabled={!tieneFicha} onChange={(e) => setFicha(e.target.checked)} /> Publicar la ficha
                {elegido?.tiene_ficha && ficha && <span style={st.alerta}> (reemplaza la que tiene)</span>}
              </label>
            </div>
            {err && <div style={{ color: colors.err, fontSize: text.sec }}>{err}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
            <Boton variante="ghost" onClick={descartar} disabled={busy}>Descartar</Boton>
            <Boton variante="primary" onClick={aprobar} disabled={busy || !elegido || (!foto && !ficha)}>
              {busy ? 'Publicando…' : '✓ Aprobar y publicar'}
            </Boton>
          </div>
        </footer>
      </div>

      {selector && (
        <SelectorArticulosModal modo="simple" titulo={`Vincular EAN ${c.ean} a un artículo`}
          onSelect={(a) => { setSelector(false); elegir(a.codigo_alfa) }} onClose={() => setSelector(false)} />
      )}
    </article>
  )
}

/** Importador: planilla con columna EAN (y SKU opcional) → vista previa → a la cola. */
function ImportarEans({ max, onClose, onListo }) {
  const [archivo, setArchivo] = useState(null)
  const [previa, setPrevia] = useState(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const leer = async (f) => {
    setArchivo(f); setPrevia(null); setErr('')
    if (!f) return
    setBusy(true)
    try {
      const { data } = await candidatosEanApi.importar(f, false)
      setPrevia(data)
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo leer la planilla.')
    } finally { setBusy(false) }
  }
  const confirmar = async () => {
    setBusy(true); setErr('')
    try {
      await candidatosEanApi.importar(archivo, true)
      onListo()
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo importar.')
      setBusy(false)
    }
  }

  const problemas = (previa?.filas || []).filter((f) => f.estado !== 'ok')
  const conSku = (previa?.filas || []).filter((f) => f.estado === 'ok' && f.sku).length

  return (
    <Modal title="Importar códigos de barras" onClose={onClose} maxWidth="720px">
      <p style={{ margin: '0 0 10px', fontSize: text.body, color: colors.textSub }}>
        Planilla Excel o CSV con una columna <strong>EAN</strong> y, si ya sabés a qué artículo va cada uno, una columna <strong>SKU</strong>
        {' '}(queda preelegido; igual se confirma al aprobar). Sin encabezados se toma la columna A como EAN y la B como SKU. Hasta {max} códigos por planilla.
      </p>
      <input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={(e) => leer(e.target.files?.[0] || null)} disabled={busy} />
      {busy && !previa && <p style={{ fontSize: text.sec, color: colors.textMeta, marginTop: 10 }}>Leyendo…</p>}
      {err && <div style={{ ...st.err, marginTop: 10 }}>{err}</div>}

      {previa && (
        <div style={{ marginTop: 14, display: 'grid', gap: 10 }}>
          <div style={{ fontSize: text.body }}>
            <strong>{previa.a_buscar}</strong> {previa.a_buscar === 1 ? 'código para buscar' : 'códigos para buscar'}
            {conSku > 0 && <span style={{ color: colors.textSec }}> ({conSku} con SKU)</span>}
            {problemas.length > 0 && <span style={{ color: colors.textSec }}> · {problemas.length} {problemas.length === 1 ? 'fila queda afuera' : 'filas quedan afuera'}</span>}
          </div>
          {problemas.length > 0 && (
            <div style={{ maxHeight: 240, overflow: 'auto', border: `1px solid ${colors.border}`, borderRadius: radius.md }}>
              <table style={st.table}>
                <thead><tr><th style={st.th}>Fila</th><th style={st.th}>EAN</th><th style={st.th}>SKU</th><th style={st.th}>Motivo</th></tr></thead>
                <tbody>
                  {problemas.map((f) => (
                    <tr key={f.fila} style={{ borderTop: `1px solid ${colors.borderSoft}` }}>
                      <td style={{ ...st.td, ...sx.mono }}>{f.fila}</td>
                      <td style={{ ...st.td, ...sx.mono }}>{f.ean}</td>
                      <td style={{ ...st.td, ...sx.mono }}>{f.sku || ''}</td>
                      <td style={{ ...st.td, color: f.estado === 'error' ? colors.err : colors.textMeta }}>{f.motivo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Boton variante="ghost" onClick={onClose} disabled={busy}>Cancelar</Boton>
            <Boton variante="primary" onClick={confirmar} disabled={busy || previa.a_buscar === 0}>
              {busy ? 'Encolando…' : `Buscar ${previa.a_buscar} ${previa.a_buscar === 1 ? 'código' : 'códigos'}`}
            </Boton>
          </div>
        </div>
      )}
    </Modal>
  )
}

const st = {
  barra: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 },
  input: { ...sx.input, width: 240, ...sx.mono },
  err: { background: colors.errBg, color: colors.err, padding: '8px 12px', borderRadius: radius.sm, marginBottom: 12, fontSize: text.body },
  aviso: { background: colors.okBg, color: colors.ok, padding: '8px 12px', borderRadius: radius.sm, marginBottom: 12, fontSize: text.body },
  cerrar: { border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, lineHeight: 1, color: 'inherit' },
  panel: { border: `1px solid ${colors.infoBorder}`, background: colors.infoSoft, padding: '12px 14px', borderRadius: radius.md, marginBottom: 12, fontSize: text.body, color: colors.text },
  panelQuieto: { border: `1px solid ${colors.border}`, background: colors.bgSoft, padding: '10px 14px', borderRadius: radius.md, marginBottom: 12, fontSize: text.body },
  panelHead: { display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' },
  spinner: { width: 14, height: 14, border: `2px solid ${colors.infoBorder}`, borderTopColor: colors.info, borderRadius: '50%', animation: 'lgr-spin 0.8s linear infinite', flexShrink: 0 },
  ahora: { marginTop: space.lg, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: radius.sm, padding: '10px 12px' },
  label: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em' },
  ahoraNombre: { fontSize: text.title, fontWeight: 600, marginTop: 2 },
  paso: { marginTop: space.xs, color: colors.info, fontWeight: 600 },
  meta: { fontSize: text.sec, color: colors.textMeta },
  trabada: { color: colors.warn, fontWeight: 600 },
  barra2: { position: 'relative', height: 4, background: colors.borderSoft, borderRadius: radius.pill, overflow: 'hidden', margin: `${space.md}px 0 ${space.sm}px` },
  barraMov: { position: 'absolute', top: 0, bottom: 0, width: '35%', background: colors.info, borderRadius: radius.pill, animation: 'lgr-barra 1.4s ease-in-out infinite' },
  reciente: { display: 'flex', alignItems: 'baseline', gap: space.sm, flexWrap: 'wrap', fontSize: text.sec },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 },
  chip: { fontSize: text.meta, padding: '2px 8px', borderRadius: radius.pill, background: colors.bg, color: colors.textSec, border: `1px solid ${colors.border}` },
  vacio: { padding: '28px 16px', color: colors.textMeta, textAlign: 'center', border: `1px dashed ${colors.border}`, borderRadius: radius.md, marginBottom: 12 },
  card: { display: 'flex', gap: 16, border: `1px solid ${colors.border}`, borderRadius: radius.md, padding: 12, background: colors.bg, flexWrap: 'wrap' },
  thumbBtn: { border: `1px solid ${colors.borderSoft}`, background: '#fff', padding: 0, borderRadius: radius.sm, cursor: 'zoom-in', width: 200, height: 200, flexShrink: 0, overflow: 'hidden' },
  thumb: { width: 200, height: 200, objectFit: 'contain', display: 'block' },
  sinImg: { width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: colors.textMeta, fontSize: text.sec },
  nombre: { fontSize: text.title, fontWeight: 600, color: colors.text, margin: '2px 0 4px' },
  alerta: { color: colors.warn, fontWeight: 600 },
  ficha: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: `${space.xs}px ${space.xl}px` },
  label: { fontSize: text.meta, fontWeight: 600, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.04em' },
  descripcion: { margin: 0, fontSize: text.body, color: colors.textSub, lineHeight: 1.5, background: colors.bgSoft, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md },
  link: { color: colors.link, textDecoration: 'none' },
  pie: { display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', borderTop: `1px solid ${colors.borderSoft}`, paddingTop: 10 },
  check: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
  h2: { margin: '28px 0 10px', fontSize: text.title, fontWeight: 600, color: colors.text },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: text.body },
  th: { textAlign: 'left', padding: '6px 8px', fontSize: text.meta, color: colors.textMeta, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 },
  td: { padding: '6px 8px', verticalAlign: 'top' },
  overlay: { position: 'fixed', inset: 0, background: colors.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 },
  zoomBox: { background: '#fff', borderRadius: radius.md, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,.35)' },
}
